/**
 * Starting shapes for a process that has none.
 *
 * A blank canvas is the hardest screen in the designer, and not because the
 * controls are hard to find: the person has to already know that a process is a
 * *sequence of steps of two kinds*, that sections go in one kind and sign-offs
 * in the other, and that the lifecycle is a second machine running alongside.
 * None of that is discoverable by clicking. A worked example teaches it in one
 * look, which is why every tool of this sort ships templates.
 *
 * These are deliberately generic. The product's bet is that internal tooling is
 * the same five things in different clothes, so the presets are the shapes that
 * recur -- a simple approval, a review-then-approve, a request with a closure
 * record -- rather than anything permit-shaped. AGP's own processes are seeds
 * (`seed_ptw_v2`), not presets, and must stay that way.
 *
 * A preset only ever describes the **stage spine**: names, kinds and where the
 * conditional steps go. It never invents sections (those are real forms with
 * real schemas, created deliberately) and never touches the lifecycle of a
 * template that already has one. Applying a preset is an ordinary edit --
 * undoable, and saved only when the person presses Save.
 */

export type PresetStage = {
  name: string;
  kind: "DataEntry" | "Approval";
  /** A note shown on the preview card, not stored anywhere. */
  hint?: string;
};

export type Preset = {
  id: string;
  name: string;
  /** One line on the card, saying when this shape is the right one. */
  description: string;
  stages: PresetStage[];
};

export const PRESETS: Preset[] = [
  {
    id: "simple-approval",
    name: "Fill in, then approve",
    description:
      "The shortest process that is still a process: somebody fills in a form and somebody else signs it off.",
    stages: [
      { name: "Request details", kind: "DataEntry", hint: "The form itself" },
      { name: "Approval", kind: "Approval", hint: "One signature" },
    ],
  },
  {
    id: "review-approve",
    name: "Review, then approve",
    description:
      "Two levels of sign-off: somebody close to the work checks it, then somebody accountable approves it. The commonest shape for purchase and change requests.",
    stages: [
      { name: "Request details", kind: "DataEntry" },
      { name: "Review", kind: "Approval", hint: "Line manager or specialist" },
      { name: "Approval", kind: "Approval", hint: "Budget or authority holder" },
    ],
  },
  {
    id: "assess-approve-close",
    name: "Assess, approve, then close out",
    description:
      "For work that has to be recorded after it is done as well as before. The closing step is data entry that only opens once the approvals are in.",
    stages: [
      { name: "Request details", kind: "DataEntry" },
      { name: "Assessment", kind: "DataEntry", hint: "Filled in by a specialist" },
      { name: "Approval", kind: "Approval" },
      {
        name: "Closure record",
        kind: "DataEntry",
        hint: "Opens only after approval",
      },
      { name: "Closure sign-off", kind: "Approval" },
    ],
  },
  {
    id: "conditional-assessment",
    name: "With a step that only sometimes applies",
    description:
      "Adds an assessment step that most cases skip. Set its rule once the questions it depends on exist — until then it runs for everybody.",
    stages: [
      { name: "Request details", kind: "DataEntry" },
      {
        name: "Extra assessment",
        kind: "DataEntry",
        hint: "Give this one a rule",
      },
      { name: "Review", kind: "Approval" },
      { name: "Approval", kind: "Approval" },
    ],
  },
];
